import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema } from '../../utils/seo';

export default function ListingMuizenbergPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // SEO Configuration
  useSEO({
    title: 'Painters Muizenberg - Beach Cottage Painting Specialists | Coastal Colors',
    description: 'Expert coastal painting services in Muizenberg, St James, and Kalk Bay. Specialized in beach cottages and vibrant coastal colors. Over 14 years of weather-resistant painting with colorful finishes.',
    keywords: 'painters Muizenberg, beach cottage painting Muizenberg, coastal painting, apartment painting, vibrant colors',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateLocalBusinessSchema({
          name: 'Painters Muizenberg',
          description: 'Beach cottage and coastal painting specialists',
          address: 'Muizenberg, Cape Town',
          priceRange: '$$',
          serviceArea: ['Muizenberg', 'St James', 'Kalk Bay', 'Fish Hoek', 'Lakeside', 'Marina da Gama']
        }),
        generateServiceSchema({
          name: 'Beach Cottage Painting',
          description: 'Specialized coastal and beach cottage painting',
          provider: 'Painters Muizenberg',
          areaServed: ['Muizenberg', 'St James', 'Kalk Bay'],
          serviceType: ['Beach Cottage Painting', 'Apartment Painting', 'Commercial Painting']
        })
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const listing = {
    name: "Painters Muizenberg",
    location: "Muizenberg, Cape Town",
    website: "http://paintersmuizenberg.co.za",
    heroImage: "https://public.readdy.ai/ai/img_res/b809b8e9d3d8befa61e1ba6d3dd85a83.jpg",
    gallery: [
      "https://readdy.ai/api/search-image?query=freshly%20painted%20beach%20cottage%20exterior%20with%20colorful%20walls%20in%20Muizenberg%20professional%20finish%20with%20coastal%20protection%20coating%20blue%20sky%20and%20beach%20background&width=800&height=600&seq=muizenberggallery1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=interior%20room%20painting%20in%20beach%20cottage%20with%20professional%20painter%20applying%20paint%20to%20walls%20using%20roller%20clean%20workspace%20with%20drop%20cloths%20and%20painting%20supplies%20beach%20view%20through%20windows&width=800&height=600&seq=muizenberggallery2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=apartment%20building%20exterior%20painting%20project%20with%20scaffolding%20professional%20painters%20working%20on%20coastal%20residential%20structure%20modern%20architecture&width=800&height=600&seq=muizenberggallery3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=beautifully%20painted%20coastal%20interior%20living%20room%20with%20feature%20wall%20elegant%20color%20scheme%20modern%20furniture%20beach%20views%20natural%20lighting%20professional%20finish&width=800&height=600&seq=muizenberggallery4&orientation=landscape"
    ],
    services: [
      {
        title: "Beach Cottage Painting",
        description: "Specialized painting services for beach cottages and coastal homes. We understand the charm of Muizenberg's iconic beach cottages and use vibrant, weather-resistant colors that capture the coastal spirit.",
        features: ["Beach cottages", "Coastal homes", "Vibrant colors", "Weather protection"]
      },
      {
        title: "Apartment Painting",
        description: "Professional painting services for apartments and residential complexes. We work efficiently with body corporates and property managers to deliver exceptional results with minimal disruption.",
        features: ["Apartment interiors", "Complex exteriors", "Body corporate approved", "Flexible scheduling"]
      },
      {
        title: "Commercial Painting",
        description: "Expert commercial painting services for shops, restaurants, and businesses. We understand the importance of maintaining your business image and work around your operating hours.",
        features: ["Retail spaces", "Restaurants & cafes", "Office spaces", "After-hours service"]
      }
    ],
    serviceAreas: ["Muizenberg", "St James", "Kalk Bay", "Fish Hoek", "Lakeside", "Marina da Gama"],
    about: "Painters Muizenberg is a leading coastal painting company serving Muizenberg and the surrounding False Bay areas. With over 14 years of experience in beach cottage and coastal property painting, we have established ourselves as the most trusted painting contractors in the area. Our team of skilled professionals specializes in vibrant coastal colors and weather-resistant finishes that capture the unique character of Muizenberg.",
    aboutExtended: [
      "We understand the unique charm and challenges of Muizenberg's coastal properties, from iconic beach cottages to modern apartments and commercial spaces. That's why we use premium weather-resistant paints in vibrant colors that not only look beautiful but also withstand the harsh coastal conditions including salt spray, wind, and intense UV exposure. Our expertise extends across beach cottages, apartments, commercial properties, and residential homes.",
      "What sets us apart is our deep appreciation for Muizenberg's coastal character and our commitment to using only the highest quality weather-resistant materials in colors that complement the area's vibrant beach culture. Our painters are fully trained in coastal painting techniques, color consultation, and the application of protective coatings. We're fully licensed and insured, giving you complete peace of mind throughout your project.",
      "We take pride in our meticulous approach to every coastal project. From initial color consultation and surface preparation to final protective coating application, we ensure that your property is not only beautifully painted but also protected against the elements for years to come. Whether you're looking to refresh your beach cottage, update your apartment, or enhance your commercial space, we deliver exceptional results that celebrate Muizenberg's unique coastal spirit."
    ],
    ourApproach: [
      "Comprehensive coastal property assessment",
      "Vibrant color consultation and selection",
      "Thorough surface preparation and repairs",
      "Premium weather-resistant paints",
      "Professional application techniques",
      "Protective sealing and finishing",
      "Post-project inspection and warranty"
    ],
    whyChoose: [
      "14+ years coastal painting experience",
      "Beach cottage specialists",
      "Vibrant color experts",
      "Fully licensed and insured",
      "Free color consultation",
      "Weather-resistant materials",
      "Body corporate approved",
      "Competitive pricing",
      "Satisfaction guaranteed"
    ],
    testimonials: [
      {
        name: "Sarah Mitchell",
        rating: 5,
        text: "Absolutely love what they did with our beach cottage! The colors are perfect and really capture the Muizenberg vibe. Professional team and excellent quality work."
      },
      {
        name: "James du Plessis",
        rating: 5,
        text: "Professional service for our apartment complex. They worked efficiently, coordinated well with our body corporate, and the results are outstanding. Highly recommended!"
      },
      {
        name: "Emma Thompson",
        rating: 5,
        text: "Best coastal painters in Muizenberg! They helped us choose the perfect colors for our beach house and the finish is beautiful. Very happy with the results."
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b border-gray-800 transition-all duration-300 ${isScrolled ? 'bg-black text-white' : 'bg-black text-white'}`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-3xl font-bold whitespace-nowrap">
              <span className="text-white">PAINTE</span>
              <span className="text-[#e9f638]">RRR</span>
            </Link>
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="hover:text-[#e9f638] transition whitespace-nowrap">Home</Link>
              <Link to="/about" className="hover:text-[#e9f638] transition whitespace-nowrap">About</Link>
              <Link to="/contact" className="bg-[#e9f638] text-black px-6 py-2 rounded-lg font-semibold hover:bg-white transition whitespace-nowrap cursor-pointer">Get a Quote</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative h-96 w-full overflow-hidden">
        <img 
          src={listing.heroImage}
          alt={listing.name}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 container mx-auto px-4 pb-8">
          <h1 className="text-5xl font-bold text-white mb-2">{listing.name}</h1>
          <p className="text-xl text-gray-200 flex items-center">
            <i className="ri-map-pin-line mr-2"></i>
            {listing.location}
          </p>
        </div>
      </div>

      {/* Contact Bar */}
      <div className="bg-[#e9f638] py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-center items-center gap-4">
            <a 
              href={listing.website}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black text-white px-8 py-3 rounded-lg font-bold hover:bg-gray-800 transition whitespace-nowrap cursor-pointer"
            >
              Visit Website
            </a>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-gray-50 border-b border-gray-200 sticky top-[73px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'overview' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'services' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'gallery' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Gallery
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'reviews' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Reviews
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {activeTab === 'overview' && (
              <div>
                <section className="mb-12">
                  <h2 className="text-3xl font-bold mb-6">About Painters Muizenberg</h2>
                  <p className="text-gray-700 text-lg leading-relaxed mb-4">{listing.about}</p>
                  {listing.aboutExtended.map((paragraph, index) => (
                    <p key={index} className="text-gray-700 text-lg leading-relaxed mb-4">{paragraph}</p>
                  ))}
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Our Professional Approach</h3>
                  <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                    <ul className="space-y-3">
                      {listing.ourApproach.map((step, index) => (
                        <li key={index} className="flex items-start">
                          <i className="ri-checkbox-circle-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                          <span className="text-gray-700 text-lg">{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Why Choose Painters Muizenberg</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {listing.whyChoose.map((reason, index) => (
                      <div key={index} className="flex items-start">
                        <i className="ri-check-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                        <span className="text-gray-700 text-lg">{reason}</span>
                      </div>
                    ))}
                  </div>
                </section>

                <section>
                  <h3 className="text-2xl font-bold mb-6">Areas We Serve</h3>
                  <p className="text-gray-700 text-lg mb-4">We proudly serve the following areas in False Bay:</p>
                  <div className="flex flex-wrap gap-3">
                    {listing.serviceAreas.map((area, index) => (
                      <span key={index} className="bg-gray-100 text-black px-4 py-2 rounded-full font-semibold">
                        {area}
                      </span>
                    ))}
                  </div>
                </section>
              </div>
            )}

            {activeTab === 'services' && (
              <div>
                <h2 className="text-3xl font-bold mb-4">Our Coastal Painting Services</h2>
                <p className="text-gray-700 text-lg mb-8">We offer comprehensive coastal painting services that celebrate Muizenberg's unique beach culture.</p>
                <div className="space-y-8">
                  {listing.services.map((service, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200 hover:border-[#e9f638] transition">
                      <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                      <p className="text-gray-700 mb-4 text-lg">{service.description}</p>
                      <h4 className="font-semibold text-lg mb-3">What We Offer:</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-gray-700">
                            <i className="ri-arrow-right-s-line text-[#e9f638] mr-2"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'gallery' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Project Gallery</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {listing.gallery.map((image, index) => (
                    <div key={index} className="h-80 w-full overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition">
                      <img 
                        src={image}
                        alt={`Project ${index + 1}`}
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500 cursor-pointer"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Customer Reviews</h2>
                <div className="space-y-6">
                  {listing.testimonials.map((testimonial, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                      <div className="flex items-center mb-4">
                        <div className="flex mr-4">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <i key={i} className="ri-star-fill text-[#e9f638] text-xl"></i>
                          ))}
                        </div>
                        <span className="font-bold text-lg">{testimonial.name}</span>
                      </div>
                      <p className="text-gray-700 text-lg leading-relaxed">{testimonial.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-24">
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-map-pin-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Service Areas</p>
                    <p className="text-gray-600">Muizenberg, St James, Kalk Bay</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-time-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Business Hours</p>
                    <p className="text-gray-600">Mon-Fri: 8AM-5PM</p>
                    <p className="text-gray-600">Sat: 9AM-1PM</p>
                  </div>
                </div>
              </div>
              
              <a 
                href={listing.website}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-[#e9f638] text-black text-center py-3 rounded-lg font-bold hover:bg-black hover:text-[#e9f638] transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Visit Website
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black text-white border-t border-gray-800">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">
                <span className="text-white">PAINTE</span>
                <span className="text-[#e9f638]">RRR</span>
              </h3>
              <p className="text-gray-400">South Africa's premier directory for professional house painters.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Service Areas</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Muizenberg</li>
                <li>St James</li>
                <li>Kalk Bay</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Connect</h4>
              <p className="text-gray-400">Find the perfect painter for your project today.</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
            <p>© 2025 PAINTERRR. All rights reserved.</p>
            <div className="flex space-x-4 mt-4 md:mt-0">
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
